import { useMemo } from "react";
import { Copy, RefreshCw, ArrowLeft, Heart, HeartOff, Share2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import useTypewriter from "../hooks/useTypewriter";
import SpeechControls from "./SpeechControls";

function readingMinutes(text) {
  const words = text.trim().split(/\s+/).length;
  const mins = Math.max(1, Math.round(words / 130)); // ~130 palabras/min lectura en voz alta para niños
  return mins;
}

export default function StoryResult({
  story,
  onRegenerate,
  onBack,
  onToggleFavorite,
  isFavorite,
  loading,
}) {
  const { visible, isDone, skip } = useTypewriter(story?.paragraphs || [], 16);

  const fullText = useMemo(
    () => (story ? `${story.title}. ${story.paragraphs.join("\n\n")}` : ""),
    [story]
  );

  const mins = useMemo(() => readingMinutes(fullText), [fullText]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      toast.success("Cuento copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const share = async () => {
    const shareData = {
      title: story.title,
      text: `🌙 ${story.title}\n\n${story.paragraphs.join("\n\n")}\n\n— Generado con Cuentos de Luna`,
    };
    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
      } catch {
        // usuario canceló
      }
    } else {
      await copy();
      toast.success("Cuento copiado — pégalo donde quieras compartirlo");
    }
  };

  if (!story) return null;

  return (
    <div className="max-w-3xl mx-auto" data-testid="story-result">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          onClick={onBack}
          data-testid="back-button"
          className="text-slate-200 hover:bg-white/10 rounded-full"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Crear otro
        </Button>
        <Button
          variant="ghost"
          onClick={onToggleFavorite}
          data-testid="favorite-button"
          className="text-slate-200 hover:bg-white/10 rounded-full"
        >
          {isFavorite ? (
            <>
              <HeartOff className="w-4 h-4 mr-2 text-rose-400" /> Quitar de favoritos
            </>
          ) : (
            <>
              <Heart className="w-4 h-4 mr-2" /> Guardar
            </>
          )}
        </Button>
      </div>

      {/* Card */}
      <div className="bg-slate-800/40 backdrop-blur-xl border border-white/10 rounded-3xl p-6 sm:p-10 shadow-[0_0_60px_rgba(167,139,250,0.08)]">
        <h1
          className="glow-title text-3xl sm:text-5xl font-bold text-center mb-3 leading-tight font-[Fredoka]"
          data-testid="story-title"
        >
          {story.title}
        </h1>
        <div className="flex items-center justify-center gap-1.5 text-slate-400 text-sm mb-8">
          <Clock className="w-3.5 h-3.5" />
          <span>≈ {mins} min de cuento</span>
        </div>

        <div className="space-y-5" data-testid="story-paragraphs">
          {visible.map((p, idx) => {
            const isLastVisible = idx === visible.length - 1 && !isDone;
            return (
              <p
                key={idx}
                className={`text-slate-100 text-lg leading-relaxed font-[Quicksand] ${
                  isLastVisible ? "caret" : ""
                }`}
              >
                {p}
              </p>
            );
          })}
        </div>

        {!isDone && (
          <div className="text-center mt-6">
            <Button
              variant="ghost"
              onClick={skip}
              data-testid="skip-typing-button"
              className="text-slate-400 hover:text-slate-200 text-sm"
            >
              Mostrar todo
            </Button>
          </div>
        )}

        {isDone && (
          <div className="flex flex-wrap gap-3 justify-center pt-8 mt-8 border-t border-white/5">
            <SpeechControls text={fullText} />
            <Button
              type="button"
              onClick={share}
              data-testid="share-button"
              className="bg-white/10 hover:bg-white/20 text-white rounded-full px-5 py-2 border border-white/10"
            >
              <Share2 className="w-4 h-4 mr-2" /> Compartir
            </Button>
            <Button
              type="button"
              onClick={copy}
              data-testid="copy-button"
              className="bg-white/10 hover:bg-white/20 text-white rounded-full px-5 py-2 border border-white/10"
            >
              <Copy className="w-4 h-4 mr-2" /> Copiar
            </Button>
            <Button
              type="button"
              onClick={onRegenerate}
              disabled={loading}
              data-testid="regenerate-button"
              className="bg-white/10 hover:bg-white/20 text-white rounded-full px-5 py-2 border border-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Generando..." : "Nuevo cuento"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
